import React, {
  useState,
  useEffect,
  ChangeEvent,
  useCallback,
  useRef,
} from "react";
import { useNavigate } from "react-router-dom";
import { Box } from "@mui/material";
import * as S from "../styles/QnaStyles";
import Pagination from "@mui/material/Pagination";
import {
  YYYY_MM_DD,
  diffInDays,
  useQuickDatePicker,
} from "../../../utils/customHooks/useDatePicker";
import { SelectChangeEvent } from "@mui/material/Select";
import { useSearchOptions } from "../../../utils/customHooks/useInputData";
import { useQuestionList } from "../../../utils/apiHooks/useQuestionData";
import { Popup } from "../../../components/popup/Popup";

interface Question {
  no: number;
  idx: number;
  email: string;
  title: string;
  status: string;
  createDate: string;
  answerDate: string;
}

export default function Notice() {
  const [questionData, setQuestionData] = useState<Question[]>([]);
  const [processStatus, setProcessStatus] = useState("all");
  const [resultStatus, setResultStatus] = useState("all");
  const [orderDirection, setOrderDirection] = useState<"asc" | "desc">("asc");
  const [orderBy, setOrderBy] = useState("no");
  const navigate = useNavigate();
  const [totalPage, setTotalPage] = useState(1);
  const [totalCnt, setTotalCnt] = useState(0);
  const [page, setPage] = useState(1);
  const [activeButton, setActiveButton] = useState("month");
  const initialRender = useRef(true);
  const { options, updateOptions } = useSearchOptions({
    sDate: "",
    eDate: "",
    // scending: "DESC",
    dateType: "create_date",
    status: "all",
    searchType: "email",
    search: "",
    limit: 15,
    page,
  });
  const [isOpenPopup, setIsOpenPopup] = useState(false);
  const [popupOptions, setPopupOptions] = useState({
    content: "권한이 없습니다.",
    type: "info",
    callback: () => {
      console.log("popup closed!");
    },
    isOpenPopup,
    setIsOpenPopup,
  });

  const { changeDate } = useQuickDatePicker(updateOptions);

  type DataItem = {
    no: number;
    email: string;
    title: string;
    answer_yn: string;
    registDate: string;
    answerDate: string;
  };

  function isKeyOfDataItem(key: any): key is keyof Question {
    return [
      "no",
      "email",
      "title",
      "status",
      "createDate",
      "answerDate",
    ].includes(key);
  }

  // useMutation : 1:1문의 목록 가져오기
  const onSuccessQuestionList = useCallback((data: any) => {
    const { results } = data.data;

    const formattedResults: Question[] = results.map((v: any, k: number) => ({
      ...v,
      no: k + 1,
      idx: v.idx,
      email: v.email,
      title: v.title,
      status: v.status,
      createDate: v.createDate,
      answerDate: v.answerDate,
    }));

    setQuestionData(formattedResults);

    console.log(data.data);

    setTotalPage(data.data.totalPage);
    setTotalCnt(data.data.totalCnt);
  }, []);

  const onErrorQuestionList = (error: any) => {
    console.log(error.data.message);
    if (
      error.data.message === "Unauthorized" ||
      error.data.message === "Forbidden"
    ) {
      alert("권한이 없습니다.");
    }
  };

  const {
    mutate: getQuestionList,
    data: questionListData,
    isSuccess,
  } = useQuestionList(onSuccessQuestionList, onErrorQuestionList);

  const handleSortRequest = (cellId: string) => {
    if (!isKeyOfDataItem(cellId)) return;

    const isAsc = orderBy === cellId && orderDirection === "asc";
    setOrderDirection(isAsc ? "desc" : "asc");
    setOrderBy(cellId);
    const sortedData = questionData.sort((a, b) => {
      if (isAsc) {
        return a[cellId] < b[cellId] ? -1 : 1;
      } else {
        return a[cellId] > b[cellId] ? -1 : 1;
      }
    });
    setQuestionData([...sortedData]);
  };

  // 페이지 변경
  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setPage(value);
    search();
  };

  // 검색 버튼
  const search = () => {
    const diff = diffInDays(options.sDate, options.eDate);

    if (options.sDate === "" || options.eDate === "") {
      alert("조회 기간을 선택해주세요");
    } else if (diff > 365) {
      alert("조회 가능 기간은 최대 1년입니다");
    } else {
      let ops;
      options.status === "all"
        ? (ops = { ...options, status: "" })
        : (ops = { ...options });
      getQuestionList(ops);
    }
  };

  const handleRowClick = (idx: number) => {
    const newUrl = `/qna/${idx}`;
    navigate(newUrl);
  };

  const handleRowCnt = (e: SelectChangeEvent<unknown>) => {
    const newLimit = Number(e.target.value);
    updateOptions("limit", newLimit);
    let ops;
    options.status === "all"
      ? (ops = { ...options, status: "", limit: newLimit })
      : (ops = { ...options, limit: newLimit });
    getQuestionList(ops);
  };

  const handleSDateChange = (e: any) => {
    const dateString = e.target.value;
    const isoFormattedDate = convertToISO8601(dateString);
    updateOptions("sDate", isoFormattedDate);
  };

  const handleEDateChange = (e: any) => {
    const dateString = e.target.value;
    const isoFormattedDate = convertToISO8601(dateString);
    updateOptions("eDate", isoFormattedDate);
  };

  const convertToISO8601 = (dateString: any) => {
    const date = new Date(dateString);
    const isoString = date.toISOString();
    return isoString;
  };

  const handlePeriodChange = (type: string) => {
    setActiveButton(type);
    changeDate(type);
  };

  useEffect(() => {
    // const today = new Date();
    // const oneWeekAgo = new Date(today);
    // oneWeekAgo.setDate(today.getDate() - 7);

    // updateOptions("sDate", YYYY_MM_DD(oneWeekAgo));
    // updateOptions("eDate", YYYY_MM_DD(today));
    changeDate("month");
  }, []);

  // 초기 렌더링 시에만 리스트 자동 호출
  useEffect(() => {
    if (initialRender.current && options.sDate && options.eDate) {
      initialRender.current = false;
      let ops;
      options.status === "all"
        ? (ops = { ...options, status: "" })
        : (ops = { ...options });
      getQuestionList(ops);
    }
  }, [options, getQuestionList]);

  return (
    <S.Container>
      <S.Typography>문의관리 {">"} 1:1문의</S.Typography>
      <S.Paper elevation={0}>
        <S.Grid
          container
          spacing={1}
          alignItems="center"
          style={{ padding: "0 20px", margin: "0" }}
        >
          <S.InputLabel>조회기간</S.InputLabel>
          <S.Grid item xs={6}>
            {" "}
            <Box
              sx={{
                mr: "10px",
                display: "flex",
                alignItems: "center",
              }}
            >
              <S.Select style={{ width: "8em" }} value="registDate">
                <S.MenuItem value="registDate">등록일</S.MenuItem>
                <S.MenuItem value="answerDate">답변일</S.MenuItem>
              </S.Select>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                mr: 2,
              }}
            >
              <S.Input
                type="date"
                id="sDate"
                name="sDate"
                style={{ width: "190px" }}
                value={YYYY_MM_DD(options.sDate)}
                onChange={handleSDateChange}
              />
              ~
              <S.Input
                type="date"
                id="eDate"
                name="eDate"
                style={{ width: "190px", marginRight: "15px" }}
                value={YYYY_MM_DD(options.eDate)}
                onChange={handleEDateChange}
              />
            </Box>
          </S.Grid>
          <S.Grid item>
            <S.NormalButton
              variant="contained"
              sx={{ mr: 1 }}
              active={activeButton === "day"}
              onClick={() => handlePeriodChange("day")}
            >
              오늘
            </S.NormalButton>
            <S.NormalButton
              variant="contained"
              sx={{ mr: 1 }}
              active={activeButton === "week"}
              onClick={() => handlePeriodChange("week")}
            >
              1주일
            </S.NormalButton>
            <S.NormalButton
              variant="contained"
              sx={{ mr: 1 }}
              active={activeButton === "month"}
              onClick={() => handlePeriodChange("month")}
            >
              1개월
            </S.NormalButton>
            <S.NormalButton
              variant="contained"
              sx={{ height: "2.5em" }}
              active={activeButton === "quarter"}
              onClick={() => handlePeriodChange("quarter")}
            >
              3개월
            </S.NormalButton>
          </S.Grid>
        </S.Grid>
        <S.Grid
          container
          spacing={1}
          alignItems="center"
          style={{ padding: "0 20px", margin: "0" }}
        >
          <S.InputLabel>답변여부</S.InputLabel>
          <S.Grid item xs={3}>
            <S.Select
              value={options.status}
              sx={{ width: "150px", height: "2.5em" }}
              onChange={(e) =>
                updateOptions("status", (e.target as HTMLSelectElement).value)
              }
            >
              <S.MenuItem value="all">전체</S.MenuItem>
              <S.MenuItem value="N">미완료</S.MenuItem>
              <S.MenuItem value="Y">완료</S.MenuItem>
            </S.Select>
          </S.Grid>
        </S.Grid>
        <S.Grid
          container
          spacing={1}
          alignItems="center"
          style={{ padding: "0 20px", margin: "0" }}
        >
          <S.InputLabel>검색</S.InputLabel>
          <S.Grid item xs={10}>
            <S.Select
              value={options.searchType}
              sx={{ width: "10em", height: "2.5em", mr: 1 }}
              onChange={(e) =>
                updateOptions(
                  "searchType",
                  (e.target as HTMLSelectElement).value
                )
              }
            >
              <S.MenuItem value="email">이메일</S.MenuItem>
              <S.MenuItem value="title">제목/내용</S.MenuItem>
              <S.MenuItem value="content">내용</S.MenuItem>
            </S.Select>
            <S.Input
              type="text"
              onChange={(e) => updateOptions("search", e.target.value)}
            />
          </S.Grid>
        </S.Grid>
        <S.Grid container spacing={1} alignItems="center">
          <S.Grid
            item
            xs={12}
            style={{
              alignItems: "center",
              justifyContent: "center",
              margin: "0",
            }}
          >
            <S.SearchButton
              variant="contained"
              color="primary"
              style={{ width: "130px" }}
              onClick={() => search()}
            >
              검색
            </S.SearchButton>
          </S.Grid>
        </S.Grid>
      </S.Paper>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "10px",
          marginTop: "30px",
        }}
      >
        <div>총 {totalCnt}건</div>
        <div>
          <S.Select defaultValue={15} sx={{ mr: 1 }} onChange={handleRowCnt}>
            <S.MenuItem value={15}>15개씩 보기</S.MenuItem>
            <S.MenuItem value={50}>50개씩 보기</S.MenuItem>
            <S.MenuItem value={100}>100개씩 보기</S.MenuItem>
          </S.Select>
        </div>
      </div>
      <S.Paper elevation={0}>
        <S.TableContainer>
          <S.Table>
            <S.TableHead>
              <S.TableHeadRow>
                <S.TableHeadCell align="center">
                  <S.TableSortLabel
                    active={orderBy === "no"}
                    hideSortIcon={false}
                    direction={
                      orderBy === "no"
                        ? ["asc", "desc"].includes(orderDirection)
                          ? orderDirection
                          : "asc"
                        : "asc"
                    }
                    onClick={() => handleSortRequest("no")}
                  >
                    No
                  </S.TableSortLabel>
                </S.TableHeadCell>
                <S.TableHeadCell align="center">이메일주소</S.TableHeadCell>
                <S.TableHeadCell align="center">문의제목</S.TableHeadCell>
                <S.TableHeadCell align="center">답변여부</S.TableHeadCell>
                <S.TableHeadCell align="center">
                  <S.TableSortLabel
                    active={orderBy === "createDate"}
                    hideSortIcon={false}
                    direction={
                      orderBy === "createDate"
                        ? ["asc", "desc"].includes(orderDirection)
                          ? orderDirection
                          : "asc"
                        : "asc"
                    }
                    onClick={() => handleSortRequest("createDate")}
                  >
                    등록일시
                  </S.TableSortLabel>
                </S.TableHeadCell>
                <S.TableHeadCell align="center">
                  <S.TableSortLabel
                    active={orderBy === "answerDate"}
                    hideSortIcon={false}
                    direction={
                      orderBy === "answerDate"
                        ? ["asc", "desc"].includes(orderDirection)
                          ? orderDirection
                          : "asc"
                        : "asc"
                    }
                    onClick={() => handleSortRequest("answerDate")}
                  >
                    답변일시
                  </S.TableSortLabel>
                </S.TableHeadCell>
              </S.TableHeadRow>
            </S.TableHead>
            <S.TableBody>
              {isSuccess && questionData.length > 0 ? (
                questionData.slice(0, 10).map((row, index) => (
                  <S.TableRow
                    key={index}
                    onClick={() => handleRowClick(row.idx)}
                  >
                    <S.TableCell align="center">{row.idx}</S.TableCell>
                    <S.TableCell align="center">{row.email}</S.TableCell>
                    <S.TableCell>{row.title}</S.TableCell>
                    <S.TableCell align="center">
                      {row.status === "Y" ? "완료" : "미완료"}
                    </S.TableCell>
                    <S.TableCell align="center">{row.createDate}</S.TableCell>
                    <S.TableCell align="center">{row.answerDate}</S.TableCell>
                  </S.TableRow>
                ))
              ) : (
                <S.TableRow>
                  <S.TableCell
                    align="center"
                    colSpan={6}
                    style={{ height: "110px" }}
                  >
                    검색 결과가 없습니다
                  </S.TableCell>
                </S.TableRow>
              )}
            </S.TableBody>
          </S.Table>
        </S.TableContainer>
      </S.Paper>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginTop: "30px",
          marginBottom: "30px",
        }}
      >
        <Pagination
          count={isSuccess && totalPage ? totalPage : 1}
          color="standard"
          onChange={handlePageChange}
          page={page}
        />
      </Box>
    </S.Container>
  );
}
